<template>
	<div
		class="bento-tile cores-tile"
		data-test="cores-tile"
	>
		<div class="bento-tile__header">
			<span class="bento-tile__eyebrow"
				>Cores
				<span
					v-if="!coresUnavailable"
					class="cores-tile__count sgnl-text-color-tertiary"
					>{{ totalCount }}</span
				></span
			>
			<v-btn
				variant="text"
				size="small"
				to="/cores"
				class="bento-tile__action"
			>
				View all
				<ArrowRight16 />
			</v-btn>
		</div>
		<ErrorEmptyState
			v-if="coresUnavailable"
			compact
			title="Could not load Cores"
			message="Often this is a temporary network or connectivity issue."
			:retrying="retryingLoad"
			@retry="retryLoad"
		/>
		<template v-else>
			<div class="cores-tile__list">
				<div
					v-for="core in visibleCores"
					:key="core.id"
					class="cores-tile__row"
				>
					<div
						class="cores-tile__icon"
						:class="`cores-tile__icon--${core.family}`"
					>
						<Chip16 />
					</div>
					<div class="cores-tile__core">
						<div class="cores-tile__name sgnl-text-color-default sgnl-caption-medium">{{ core.name }}</div>
						<div class="cores-tile__spec sgnl-text-color-secondary sgnl-footnote-regular">
							{{ core.spec }}
						</div>
					</div>
				</div>
				<router-link
					v-if="overflow > 0"
					to="/cores"
					class="cores-tile__overflow sgnl-text-color-secondary sgnl-footnote-regular"
					>+ {{ overflow }} more cores</router-link
				>
			</div>
			<div
				v-if="customCoresCount === 0"
				class="cores-tile__custom-prompt sgnl-text-color-secondary sgnl-footnote-regular"
			>
				No custom cores yet.
			</div>
		</template>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";
import Chip16 from "@carbon/icons-vue/es/chip/16";

import ErrorEmptyState from "@/components/Common/ErrorEmptyState.vue";
import { mapState } from "pinia";
import { useCoresStore } from "@/stores/cores";
import { MINIMUM_RETRY_FEEDBACK_MS, toStringWithByteMagnitude, withMinimumDuration } from "@/js/utilities";
import type { Core } from "@/types/api/cores";

const isUsable = (c: Core): boolean => !c.Disabled && !c.Deprecated && !c.Hidden;

type CoreRow = {
	id: string;
	name: string;
	spec: string;
	family: "pro" | "default";
	isCustom: boolean;
};

export default defineComponent({
	name: "CoresTile",
	components: { ArrowRight16, Chip16, ErrorEmptyState },
	props: {
		customCores: { type: Number, default: 0 },
	},
	data: () => ({
		retryingLoad: false,
	}),
	setup() {
		return { coresStore: useCoresStore() };
	},
	methods: {
		async retryLoad(): Promise<void> {
			this.retryingLoad = true;
			try {
				await withMinimumDuration(this.coresStore.getCoresFilteredCoresByUserTier(), MINIMUM_RETRY_FEEDBACK_MS);
			} finally {
				this.retryingLoad = false;
			}
		},
	},
	computed: {
		...mapState(useCoresStore, {
			filteredCores: "filteredCoresList",
			formatArch: "coreMicroarchitectureStringFormat",
		}),
		coresUnavailable(): boolean {
			return (
				this.coresStore.userTierResolutionFailed ||
				(this.coresStore.loadError && this.coresStore.defaultCoresList === undefined)
			);
		},
		customCoresCount(): number {
			return this.customCores;
		},
		coreRows(): CoreRow[] {
			const list = ((this.filteredCores as Core[] | undefined) ?? []).filter(isUsable);
			const rows = list.map((c) => ({
				id: c.CoreID,
				name: c.Name,
				spec: `${this.formatArch(c.Microarchitecture, c.Precision)} · ${toStringWithByteMagnitude(c.MemorySize)}`,
				family: c.Class === "C0Pro" ? ("pro" as const) : ("default" as const),
				isCustom: c.Owner !== "*",
			}));
			// Custom cores first, then defaults
			return rows.sort((a, b) => Number(b.isCustom) - Number(a.isCustom));
		},
		visibleCores(): CoreRow[] {
			return this.coreRows.slice(0, 3);
		},
		totalCount(): number {
			return this.coreRows.length;
		},
		overflow(): number {
			return Math.max(0, this.totalCount - this.visibleCores.length);
		},
	},
});
</script>

<style lang="scss" scoped>
.cores-tile__list {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-10);
}

.cores-tile__row {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-10);
	padding: var(--sgnl-space-8);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-tertiary-border);
	border-radius: var(--sgnl-border-radius-6);
}

.cores-tile__icon {
	width: var(--sgnl-icon-28);
	height: var(--sgnl-icon-28);
	border-radius: var(--sgnl-border-radius-6);
	display: grid;
	place-items: center;
	flex-shrink: 0;
}

.cores-tile__icon--default {
	background: var(--sgnl-brand-background-inverse);
	color: var(--sgnl-brand-icon);
}

.cores-tile__icon--pro {
	background: var(--accent-ac-purple-50);
	color: var(--accent-ac-purple-500-base);
}

.cores-tile__core {
	flex: 1;
	min-width: 0;
}

.cores-tile__name {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.cores-tile__spec {
	font-family: var(--sgnl-font-family-mono);
}

.cores-tile__overflow {
	text-align: center;
	padding: var(--sgnl-space-4) 0;
	text-decoration: none;
}

.cores-tile__custom-prompt {
	padding-top: var(--sgnl-space-10);
	border-top: var(--sgnl-border-width-1) solid var(--sgnl-default-tertiary-border);
}
</style>
